/**
 * CRM Accounts API client
 * Proxy: /api/crm/accounts → crm-service:3025
 */

const BASE = '/api/crm';

export interface AccountActivity {
  pageviews_30d: number;
  unique_users_30d: number;
  sessions_30d: number;
  top_pages: { page: string; views: number }[];
  source: string;
}

export interface AccountRecord {
  id: string;
  name: string;
  domain: string | null;
  workos_org_id: string;
  workos_org_name?: string;
  workspace_slug?: string | null; // Martha workspace slug — populated when org is linked
  member_count: number;
  active_members: number;
  last_login_at: string | null;
  onboarded_at: string | null;
  health_score: number;
  risk_level: 'healthy' | 'at_risk' | 'critical';
  contact_count: number;
  record_source: string;
  source_detail: string | null;
  // Enriched fields (from PostHog batch)
  pageviews_30d?: number;
  open_tickets?: number;
}

export interface AccountUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  company_id: string | null;
  company_name: string | null;
  job_title: string | null;
  workos_status: 'active' | 'invited' | 'inactive';
  last_sign_in_at: string | null;
  membership_role: string;
  profile_picture_url: string | null;
  email_verified: boolean;
  pageviews_30d?: number;
  sessions_30d?: number;
}

export interface AccountDetail extends AccountRecord {
  users: AccountUser[];
  activity: AccountActivity | null;
  support: { open_tickets: number; resolved_tickets: number };
  deals: { count: number; won_revenue: number; open_deals: number };
  comms: { email_count: number; last_email_at: string | null };
}

export interface AccountContact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  job_title: string | null;
  phone: string | null;
  lifecycle_stage: string | null;
  company_name: string | null;
  record_source: string;
}

export interface AccountEmail {
  id: string;
  subject: string | null;
  from_name: string | null;
  from_address: string | null;
  to_addresses: string[] | null;
  direction: 'inbound' | 'outbound';
  body_preview: string | null;
  sent_at: string | null;
  thread_id: string | null;
}


export interface AccountsResponse {
  data: AccountRecord[];
  total: number;
}

export interface AccountUsersResponse {
  data: AccountUser[];
  total: number;
}

async function request<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Accounts API ${res.status}: ${res.statusText}`);
  return res.json() as Promise<T>;
}

export const accountsApi = {
  list: (params?: { search?: string; limit?: number; offset?: number }): Promise<AccountsResponse> => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<AccountsResponse>(`/accounts${suffix}`);
  },

  get: (id: string): Promise<AccountDetail> => {
    return request<AccountDetail>(`/accounts/${id}`);
  },

  listUsers: (params?: { search?: string; limit?: number; offset?: number; company_id?: string }): Promise<AccountUsersResponse> => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    if (params?.company_id) qs.set('company_id', params.company_id);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<AccountUsersResponse>(`/accounts/users${suffix}`);
  },

  listContacts: (id: string): Promise<{ data: AccountContact[] }> => {
    return request<{ data: AccountContact[] }>(`/accounts/${id}/contacts`);
  },

  listEmails: (id: string, limit = 10): Promise<{ data: AccountEmail[] }> => {
    return request<{ data: AccountEmail[] }>(`/accounts/${id}/emails?limit=${limit}`);
  },
};
